import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { validateEmail } from "@/utils/formValidation";
import { authLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";

interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ForgotPasswordDialog = ({
  open,
  onOpenChange,
}: ForgotPasswordDialogProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { requestPasswordResetAsync } = useAuth();
  const emailValidation = useMemo(() => validateEmail(email), [email]);

  const { language: lang } = useLanguage();
  const labels = useMemo(() => authLabels(lang), [lang]);

  const { isNative } = useNativePlatform();

  const handleSubmit = async () => {
    if (!emailValidation.isValid) {
      toast.error(emailValidation.errors[0] ?? labels.error?.invalidEmail);
      return;
    }
    setIsSubmitting(true);
    try {
      await requestPasswordResetAsync(email);
      toast.success(labels.success?.passwordResetEmailSent);
      onOpenChange(false);
      setEmail("");
    } catch (e: unknown) {
      const message =
        (e as { message?: string })?.message ??
        labels.error?.couldNotSendPasswordReset;
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("sm:max-w-md", isNative && "native-sheet-form")}
        sheetOpen={open}
        compact
      >
        <DialogHeader>
          <DialogTitle>
            {labels.forgotPassword?.title || labels.title?.forgotPassword}
          </DialogTitle>
          <DialogDescription>
            {labels.forgotPassword?.description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 native-sheet-form">
          <Input
            type="email"
            placeholder={labels.forms?.fields?.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={
              !email || emailValidation.isValid ? undefined : "border-red-500"
            }
          />
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!emailValidation.isValid || isSubmitting}
          >
            {isSubmitting
              ? labels.buttons?.sendingPasswordReset
              : labels.buttons?.sendPasswordResetEmail}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
